// Salon-Kopf: ersetzt das alte horizontale Config-Panel an `/new` durch
// eine warmere, zweispaltige Header-Komposition.
//
//   Links:  Headline „Wir haben für dich schon alles eingestellt."
//           plus Termin-Dauer als Pillen-Auswahl (30/45/60/90/120 min + Custom).
//   Rechts: „Detail-Einstellungen" — Datum-Bereich, Tageszeit-Slider, Wochentage,
//           Raster, Feiertage. Alle Inputs schreiben direkt in den Store.
//
// Matrix, Terminliste, Share-Panel etc. bleiben unverändert und werden
// von `salon-builder-view.ts` darunter gemountet.

import type { Store } from '../store';
import type { FinderConfig, HolidayCountry } from '../types';
import { WEEKDAY_LABELS_DE } from '../time';
import { createTimeRangePicker } from './time-range-picker';
import { openDateRangePicker } from './date-range-picker';
import { el } from './dom';

const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DURATION_PILLS = [30, 45, 60, 90, 120];
const GRANULARITIES: Array<FinderConfig['slotSizeMinutes']> = [15, 30, 60];

const COUNTRY_OPTIONS: Array<{ value: HolidayCountry; label: string; flag: string }> = [
  { value: 'DE', label: 'Deutschland', flag: '🇩🇪' },
  { value: 'AT', label: 'Österreich',  flag: '🇦🇹' },
  { value: 'CH', label: 'Schweiz',     flag: '🇨🇭' },
];

function formatIsoForButton(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return date.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
}

function oneMonthAfter(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setMonth(dt.getMonth() + 1);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Minuten → menschlich lesbar: 75 → „1 h 15 min", 60 → „1 h", 30 → „30 min". */
function readableDuration(min: number): string {
  if (!Number.isFinite(min) || min <= 0) return '';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** Grammatik: 1 h → „wäre"; alles andere (mehrere Stunden ODER reine Min) → „wären". */
function durationVerb(min: number): 'wäre' | 'wären' {
  return Math.floor(min / 60) === 1 ? 'wäre' : 'wären';
}

export function renderSalonConfigHead(store: Store): HTMLElement {
  const root = el('section', { class: 'salon-head', 'aria-label': 'Termin-Konfiguration' });

  // ── LINKS ─────────────────────────────────────────────────────────
  const left = el('div', { class: 'salon-head-left' });
  left.append(
    el('p', { class: 'salon-eyebrow accent' }, 'Dein neuer Termin'),
    el('h1', { class: 'salon-h1' }, 'Wir haben für dich schon alles eingestellt.'),
  );

  const durationBlock = el('section', { class: 'salon-duration-block' });
  durationBlock.append(el('h2', { class: 'salon-h2' }, 'Wie lange dauert dein Termin?'));

  const durationPillsRow = el('div', { class: 'pills salon-duration-pills' });
  const durationButtons = new Map<number, HTMLButtonElement>();
  for (const min of DURATION_PILLS) {
    const btn = el('button',
      { type: 'button', class: 'pill', 'data-dur': String(min) },
      `${min} min`,
    ) as HTMLButtonElement;
    btn.addEventListener('click', () => {
      store.patchConfig({ defaultDurationMinutes: min });
    });
    durationButtons.set(min, btn);
    durationPillsRow.append(btn);
  }
  const customLabel = el('label', {
    class: 'pill pill-input salon-duration-custom-pill',
    title: 'Eigene Dauer in Minuten eingeben',
  });
  const customInput = el('input', {
    type: 'number', min: '5', max: '480', step: '5', placeholder: '180',
    class: 'salon-duration-custom-val',
    'aria-label': 'Eigene Dauer in Minuten',
  }) as HTMLInputElement;
  customInput.addEventListener('input', () => {
    const v = Number(customInput.value);
    if (Number.isFinite(v) && v > 0) store.patchConfig({ defaultDurationMinutes: v });
  });
  customLabel.append(customInput, el('span', { class: 'sub' }, 'min'));
  durationPillsRow.append(customLabel);

  const readable = el('p', {
    class: 'salon-duration-readable',
    'aria-live': 'polite',
  }, '');

  durationBlock.append(durationPillsRow, readable);
  left.append(durationBlock);

  // Hinweis unter dem Dauer-Block: deutet auf den Rest der Seite (Matrix etc.).
  left.append(
    el('div', { class: 'salon-next-hint' },
      el('span', { class: 'salon-next-hint-text' }, 'gleich weiter nach unten'),
      el('span', { class: 'salon-next-hint-arrow' }, '↓'),
    ),
  );

  // ── RECHTS ────────────────────────────────────────────────────────
  const right = el('div', { class: 'salon-head-right' });
  right.append(
    el('p', { class: 'salon-eyebrow accent' }, 'Detail-Einstellungen'),
    el('p', { class: 'salon-head-ignore' },
      'Das hier kannst du ignorieren — wahrscheinlich passt das alles, wie es ist.'),
    el('h2', { class: 'salon-h2 salon-head-still' }, 'Möchtest du trotzdem etwas ändern?'),
  );

  // ── Sektion 1: Zeitraum (Datums-Buttons) ──
  const dateBlock = el('section', { class: 'salon-section' });
  const dateRow = el('div', { class: 'salon-date-row' });

  let endTouched = false;

  const dateFromBtn = el('button',
    { type: 'button', class: 'salon-date-btn', 'aria-label': 'Von-Datum auswählen' },
    el('span', { class: 'salon-date-cap' }, 'Von'),
    el('span', { class: 'salon-date-val' }, '—'),
  ) as HTMLButtonElement;
  dateFromBtn.addEventListener('click', () => {
    openDateRangePicker({
      value: store.get().config.rangeStart,
      anchor: dateFromBtn,
      onPick: (iso) => {
        const patch: Partial<FinderConfig> = { rangeStart: iso };
        if (!endTouched) patch.rangeEnd = oneMonthAfter(iso);
        store.patchConfig(patch);
      },
    });
  });

  const dateToBtn = el('button',
    { type: 'button', class: 'salon-date-btn', 'aria-label': 'Bis-Datum auswählen' },
    el('span', { class: 'salon-date-cap' }, 'Bis'),
    el('span', { class: 'salon-date-val' }, '—'),
  ) as HTMLButtonElement;
  dateToBtn.addEventListener('click', () => {
    openDateRangePicker({
      value: store.get().config.rangeEnd,
      anchor: dateToBtn,
      onPick: (iso) => {
        endTouched = true;
        store.patchConfig({ rangeEnd: iso });
      },
    });
  });

  dateRow.append(dateFromBtn, dateToBtn);
  dateBlock.append(
    el('p', { class: 'salon-eyebrow' }, 'Zeitraum'),
    dateRow,
  );
  right.append(dateBlock);

  // ── Sektion 2: Tageszeit (echter Time-Range-Picker) ──
  const initialConfig = store.get().config;
  const timeRange = createTimeRangePicker({
    dayStart: initialConfig.dayStartMinutes,
    dayEnd:   initialConfig.dayEndMinutes,
    onChange: (start, end) => store.patchConfig({ dayStartMinutes: start, dayEndMinutes: end }),
  });
  right.append(
    el('section', { class: 'salon-section' },
      el('p', { class: 'salon-eyebrow' }, 'Tageszeit'),
      timeRange.root,
    ),
  );

  // ── Sektion 3: Wochentage ──
  const weekdayBlock = el('section', { class: 'salon-section' });
  weekdayBlock.append(el('p', { class: 'salon-eyebrow' }, 'An welchen Tagen?'));
  const weekdayRow = el('div', { class: 'pills salon-weekday-row' });
  const weekdayButtons = new Map<number, HTMLButtonElement>();
  for (const day of WEEKDAY_ORDER) {
    const isWeekend = day === 0 || day === 6;
    const btn = el('button',
      { type: 'button', class: `pill is-day${isWeekend ? ' is-weekend' : ''}` },
      WEEKDAY_LABELS_DE[day],
    ) as HTMLButtonElement;
    btn.addEventListener('click', () => {
      const current = new Set(store.get().config.allowedWeekdays);
      if (current.has(day)) current.delete(day);
      else current.add(day);
      store.patchConfig({ allowedWeekdays: [...current].sort() });
    });
    weekdayButtons.set(day, btn);
    weekdayRow.append(btn);
  }
  weekdayBlock.append(weekdayRow);

  const presetRow = el('div', { class: 'pills salon-weekday-presets' });
  const presetButtons: Array<{ btn: HTMLButtonElement; days: number[] }> = [];
  const presetData: Array<[string, number[]]> = [
    ['Werktage', [1, 2, 3, 4, 5]],
    ['Wochenende', [0, 6]],
    ['Alle Tage', [0, 1, 2, 3, 4, 5, 6]],
  ];
  for (const [label, days] of presetData) {
    const btn = el('button', { type: 'button', class: 'pill' }, label) as HTMLButtonElement;
    btn.addEventListener('click', () => store.patchConfig({ allowedWeekdays: days.slice() }));
    presetButtons.push({ btn, days: days.slice().sort() });
    presetRow.append(btn);
  }
  weekdayBlock.append(presetRow);
  right.append(weekdayBlock);

  // ── Sektion 4: Raster ──
  const granularityBlock = el('section', { class: 'salon-section' });
  granularityBlock.append(el('p', { class: 'salon-eyebrow' }, 'Im Raster von'));
  const granularityRow = el('div', { class: 'pills salon-raster-row' });
  const granularityButtons = new Map<number, HTMLButtonElement>();
  for (const g of GRANULARITIES) {
    const btn = el('button',
      { type: 'button', class: 'pill', 'data-raster': String(g) },
      `${g} min`,
    ) as HTMLButtonElement;
    btn.addEventListener('click', () => {
      const cur = store.get().config;
      const snappedDuration = Math.max(g, Math.round(cur.defaultDurationMinutes / g) * g);
      store.patchConfig({ slotSizeMinutes: g, defaultDurationMinutes: snappedDuration });
    });
    granularityButtons.set(g, btn);
    granularityRow.append(btn);
  }
  granularityBlock.append(granularityRow);
  right.append(granularityBlock);

  // ── Sektion 5: Feiertage ──
  const countryBlock = el('section', { class: 'salon-section' });
  countryBlock.append(el('p', { class: 'salon-eyebrow' }, 'Feiertage'));
  const countryRow = el('div', { class: 'pills salon-country-row' });
  const countryButtons = new Map<HolidayCountry, HTMLButtonElement>();
  for (const opt of COUNTRY_OPTIONS) {
    const btn = el('button', {
      type: 'button',
      class: 'pill is-flag',
      title: opt.label,
      'aria-label': opt.label,
      'aria-pressed': 'false',
    }, opt.flag) as HTMLButtonElement;
    btn.addEventListener('click', () => {
      const cur = new Set(store.get().config.holidayCountries);
      if (cur.has(opt.value)) cur.delete(opt.value);
      else cur.add(opt.value);
      store.patchConfig({ holidayCountries: [...cur].sort() as HolidayCountry[] });
    });
    countryButtons.set(opt.value, btn);
    countryRow.append(btn);
  }
  countryBlock.append(countryRow);
  right.append(countryBlock);

  root.append(left, right);

  // ── Reaktiv auf Store-Updates ─────────────────────────────────────
  store.subscribe((s) => {
    const c = s.config;

    // Datums-Buttons.
    dateFromBtn.querySelector('.salon-date-val')!.textContent = formatIsoForButton(c.rangeStart);
    dateToBtn.querySelector('.salon-date-val')!.textContent   = formatIsoForButton(c.rangeEnd);

    // Time-Range.
    timeRange.update(c.dayStartMinutes, c.dayEndMinutes);

    // Wochentage.
    const activeDays = new Set(c.allowedWeekdays);
    for (const [day, btn] of weekdayButtons) {
      btn.classList.toggle('is-selected', activeDays.has(day));
    }
    const activeArr = [...activeDays].sort();
    for (const { btn, days } of presetButtons) {
      const matches = activeArr.length === days.length
        && activeArr.every((v, i) => v === days[i]);
      btn.classList.toggle('is-selected', matches);
    }

    // Raster.
    for (const [g, btn] of granularityButtons) {
      btn.classList.toggle('is-selected', g === c.slotSizeMinutes);
    }

    // Feiertage.
    const activeCountries = new Set(c.holidayCountries);
    for (const [code, btn] of countryButtons) {
      const on = activeCountries.has(code);
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-pressed', String(on));
    }

    // Dauer-Pillen — eine ist aktiv, wenn der Wert exakt einem Preset
    // entspricht; sonst ist die Custom-Pill aktiv und der Wert sitzt im Input.
    const dur = c.defaultDurationMinutes;
    let matched = false;
    for (const [min, btn] of durationButtons) {
      const on = min === dur;
      btn.classList.toggle('is-selected', on);
      if (on) matched = true;
    }
    customLabel.classList.toggle('is-selected', !matched);
    if (!matched && document.activeElement !== customInput) {
      customInput.value = String(dur);
    } else if (matched && document.activeElement !== customInput) {
      customInput.value = '';
    }

    // Live-Umrechnung.
    readable.textContent = `das ${durationVerb(dur)} ${readableDuration(dur)}`;
  });

  // Placeholder-Cycle, damit das Custom-Pill als Eingabefeld erkennbar wird.
  const samples = ['180', '15', '240'];
  let idx = 0;
  customInput.placeholder = samples[0];
  setInterval(() => {
    if (document.activeElement === customInput) return;
    if (customInput.value) return;
    idx = (idx + 1) % samples.length;
    customInput.placeholder = samples[idx];
  }, 1800);

  return root;
}
